import React from 'react';
import { Sun, Moon, Monitor } from 'lucide-react';
import { useTheme } from '@/hooks/useTheme';
import { motion, AnimatePresence } from 'framer-motion';

interface ThemeToggleProps {
  variant?: 'icon' | 'segmented';
  className?: string;
}

export const ThemeToggle: React.FC<ThemeToggleProps> = ({ 
  variant = 'icon',
  className = '' 
}) => {
  const { theme, setTheme, resolvedTheme, toggleTheme } = useTheme();

  if (variant === 'segmented') {
    return (
      <div className={`inline-flex items-center p-1 rounded-full bg-bg-tertiary border border-border-subtle ${className}`}>
        <button
          onClick={() => setTheme('light')}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-body-s font-medium transition-all duration-200 ${
            theme === 'light'
              ? 'bg-apple-blue text-white shadow-sm'
              : 'text-text-secondary hover:text-text-primary'
          }`}
          aria-label="Light mode"
        >
          <Sun className="w-4 h-4" />
          <span className="hidden sm:inline">Light</span>
        </button>
        
        <button
          onClick={() => setTheme('system')}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-body-s font-medium transition-all duration-200 ${
            theme === 'system'
              ? 'bg-apple-blue text-white shadow-sm'
              : 'text-text-secondary hover:text-text-primary'
          }`}
          aria-label="System preference"
        >
          <Monitor className="w-4 h-4" />
          <span className="hidden sm:inline">Auto</span>
        </button>
        
        <button
          onClick={() => setTheme('dark')}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-body-s font-medium transition-all duration-200 ${
            theme === 'dark'
              ? 'bg-apple-blue text-white shadow-sm'
              : 'text-text-secondary hover:text-text-primary'
          }`}
          aria-label="Dark mode"
        >
          <Moon className="w-4 h-4" />
          <span className="hidden sm:inline">Dark</span>
        </button>
      </div>
    );
  }

  // Icon variant (default)
  return (
    <button
      onClick={toggleTheme}
      className={`
        relative w-10 h-10 rounded-full flex items-center justify-center
        bg-bg-tertiary border border-border-subtle
        hover:border-apple-blue/50 hover:bg-bg-secondary
        transition-all duration-200
        ${className}
      `}
      aria-label={`Switch to ${resolvedTheme === 'dark' ? 'light' : 'dark'} mode`}
      title={`Switch to ${resolvedTheme === 'dark' ? 'light' : 'dark'} mode`}
    >
      <AnimatePresence mode="wait" initial={false}>
        {resolvedTheme === 'dark' ? (
          <motion.div
            key="moon"
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 20, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <Moon className="w-5 h-5 text-apple-blue" />
          </motion.div>
        ) : (
          <motion.div
            key="sun"
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 20, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <Sun className="w-5 h-5 text-apple-blue" />
          </motion.div>
        )}
      </AnimatePresence>
    </button>
  );
};

export default ThemeToggle;
